'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface Category {
  id: string;
  name: string;
  image: string;
  alt: string;
  span: string;
}

const categories: Category[] = [
{
  id: 'cat_weddings',
  name: 'Weddings',
  image: "https://images.unsplash.com/photo-1630782273375-405a1a109ea8",
  alt: 'Bride in white lace gown holding pink rose bouquet at outdoor wedding ceremony',
  span: 'lg:col-span-2 lg:row-span-2'
},
{
  id: 'cat_portraits',
  name: 'Portraits',
  image: "https://images.unsplash.com/photo-1630520901951-707090ccac66",
  alt: 'Young woman in denim jacket with natural makeup posing against white brick wall',
  span: 'lg:col-span-1'
},
{
  id: 'cat_events',
  name: 'Events',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_142cf3ce8-1766846549077.png",
  alt: 'Elegant corporate gala with guests in formal attire under golden chandelier lighting',
  span: 'lg:col-span-1'
},
{
  id: 'cat_commercial',
  name: 'Commercial',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f26182-1767396332346.png",
  alt: 'Professional product photography setup with luxury watch on marble surface',
  span: 'lg:col-span-1'
},
{
  id: 'cat_fashion',
  name: 'Fashion',
  image: "https://images.unsplash.com/photo-1620352511970-fdc63b5eda59",
  alt: 'Fashion model in designer black dress on urban rooftop with city skyline background',
  span: 'lg:col-span-1'
}];


export default function FeaturedCategories() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section data-component-id="src/app/homepage/components/FeaturedCategories.tsx:75:4" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="75" data-component-end-line="108" data-component-file="FeaturedCategories.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20md%3Apy-32%20px-6%20md%3Apx-12%20max-w-container%20mx-auto%22%7D" ref={sectionRef} className="py-20 md:py-32 px-6 md:px-12 max-w-container mx-auto">
      <div data-component-id="src/app/homepage/components/FeaturedCategories.tsx:76:6" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="76" data-component-end-line="83" data-component-file="FeaturedCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%20reveal%22%7D" className="mb-12 reveal">
        <h2 data-component-id="src/app/homepage/components/FeaturedCategories.tsx:77:8" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="77" data-component-end-line="79" data-component-file="FeaturedCategories.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-serif%20font-semibold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Explore%20Our%20Work%22%7D" className="text-4xl md:text-5xl font-serif font-semibold text-foreground mb-4">
          Explore Our Work
        </h2>
        <p data-component-id="src/app/homepage/components/FeaturedCategories.tsx:80:8" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="80" data-component-end-line="82" data-component-file="FeaturedCategories.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20max-w-2xl%22%2C%22textContent%22%3A%22From%20intimate%20moments%20to%20grand%20celebrations%2C%20we%20capture%20it%20all%20with%20artistic%20precision.%22%7D" className="text-lg text-muted-foreground max-w-2xl">
          From intimate moments to grand celebrations, we capture it all with artistic precision.
        </p>
      </div>

      <div data-component-id="src/app/homepage/components/FeaturedCategories.tsx:85:6" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="85" data-component-end-line="107" data-component-file="FeaturedCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-4%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category, index) =>
        <Link data-component-id="src/app/homepage/components/FeaturedCategories.tsx:87:8" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="87" data-component-end-line="105" data-component-file="FeaturedCategories.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
          key={category.id}
          href={`/portfolio?category=${category.name.toLowerCase()}`}
          className={`relative group overflow-hidden rounded-2xl ${category.span} reveal delay-${(index + 1) * 100}`}>

            <div data-component-id="src/app/homepage/components/FeaturedCategories.tsx:92:12" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="92" data-component-end-line="104" data-component-file="FeaturedCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22aspect-square%20lg%3Aaspect-auto%20lg%3Ah-full%20relative%20overflow-hidden%22%7D" className="aspect-square lg:aspect-auto lg:h-full relative overflow-hidden">
              <AppImage data-component-id="src/app/homepage/components/FeaturedCategories.tsx:93:14" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="93" data-component-end-line="96" data-component-file="FeaturedCategories.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%20group-hover%3Ascale-105%20transition-transform%20duration-500%22%7D"
              src={category.image}
              alt={category.alt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

              <div data-component-id="src/app/homepage/components/FeaturedCategories.tsx:98:14" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="98" data-component-end-line="98" data-component-file="FeaturedCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-t%20from-black%2F60%20to-transparent%22%7D" className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div data-component-id="src/app/homepage/components/FeaturedCategories.tsx:99:14" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="99" data-component-end-line="103" data-component-file="FeaturedCategories.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-6%20left-6%22%7D" className="absolute bottom-6 left-6">
                <h3 data-component-id="src/app/homepage/components/FeaturedCategories.tsx:100:16" data-component-path="src/app/homepage/components/FeaturedCategories.tsx" data-component-line="100" data-component-end-line="102" data-component-file="FeaturedCategories.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-2xl%20md%3Atext-3xl%20font-serif%20font-semibold%20text-white%22%7D" className="text-2xl md:text-3xl font-serif font-semibold text-white">
                  {category.name}
                </h3>
              </div>
            </div>
          </Link>
        )}
      </div>
    </section>);

}